import InfoIcon from '@mdi/svg/svg/information-outline.svg?react';
import { MDIIcon } from '@solinkcorp/solink-components';
import { Card, Tooltip, TooltipContent, TooltipTrigger, cn } from '@solinkcorp/solink-ui';
import classNames from 'classnames';
import { Info } from 'lucide-react';
import React from 'react';
import styles from './SpotCheckCard.module.sass';

interface SpotCheckCardProps {
  title: string;
  onClick?: () => void;
  templateThumbnail?: string;
  description?: string;
  /** Fill the grid/flex cell instead of a fixed 320px width. */
  fluid?: boolean;
  variant?: 'legacy' | 'aspen';
}

export default function SpotCheckCard(props: SpotCheckCardProps) {
  const {
    title,
    onClick,
    templateThumbnail,
    description,
    fluid = false,
    variant = 'legacy',
  } = props;
  const [isInfoHovered, setIsInfoHovered] = React.useState(false);

  if (variant === 'aspen') {
    return (
      <Card
        variant="elevated"
        padding="none"
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onClick?.();
          }
        }}
        className={cn(
          'tw min-w-0 gap-0 self-start border-border-muted bg-background-surface',
          fluid ? 'w-full max-w-[20rem]' : 'w-[20rem] max-w-full',
        )}
      >
        <div className="tw relative h-40 w-full shrink-0 overflow-hidden rounded-t-lg">
          {templateThumbnail ? (
            <img src={templateThumbnail} alt="" className="size-full object-cover object-center" />
          ) : null}
        </div>
        <div className="tw flex min-h-12 items-center gap-2 border-t border-border-muted px-3 py-2">
          <span className="tw min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap py-px text-xs font-medium leading-5 text-foreground-primary">
            {title}
          </span>
          {description ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="tw shrink-0 text-foreground-muted hover:text-foreground-secondary"
                  aria-label={description}
                  onClick={(event) => event.stopPropagation()}
                >
                  <Info className="size-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="tw max-w-xs text-left">
                {description}
              </TooltipContent>
            </Tooltip>
          ) : null}
        </div>
      </Card>
    );
  }

  const handleMouseEnter = () => setIsInfoHovered(true);
  const handleMouseLeave = () => setIsInfoHovered(false);

  return (
    <div className={classNames(styles.spotCheckCard, { [styles.fluid]: fluid })} onClick={onClick}>
      <div className={styles.spotCheckCardImageWrapper}>
        <img src={templateThumbnail} alt="" />
        <div className={classNames(styles.imageOverlay, { [styles.visible]: isInfoHovered })}>
          {description}
        </div>
      </div>
      <div className={styles.spotCheckCardTitleWrapper}>
        {title}
        <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
          <MDIIcon icon={InfoIcon} size={18} />
        </div>
      </div>
    </div>
  );
}
